"use client";

import { MoreVertical } from "lucide-react";
import { useRef, useState } from "react";
import { Popover } from "@/components/ui/Popover";
import { twMerge } from "tailwind-merge";

export interface ActionMenuItem {
  label: string;
  icon?: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  onClick: () => void;
  variant?: "default" | "destructive";
  testId?: string;
}

interface ActionMenuProps {
  items: ActionMenuItem[];
  triggerLabel: string;
  testId?: string;
}

export function ActionMenu({ items, triggerLabel, testId }: ActionMenuProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <div className="shrink-0">
      <button
        ref={triggerRef}
        type="button"
        aria-label={triggerLabel}
        data-testid={testId}
        onClick={() => setOpen((v) => !v)}
        className={twMerge(
          "flex size-8 cursor-pointer items-center justify-center rounded-lg border-0 transition-colors duration-150",
          open ? "bg-brand-gray" : "hover:bg-brand-gray bg-transparent",
        )}
      >
        <MoreVertical className="text-content-muted size-4" strokeWidth={2} />
      </button>

      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchor={triggerRef}
        placement="bottom-end"
        offset={4}
        testId={testId && `${testId}-menu`}
        className="animate-v5-overlay-fade-in-menu rounded-input min-w-40 overflow-hidden border bg-white shadow-[0_4px_16px_rgba(0,0,0,0.1)]"
      >
        {items.map((item) => {
          const Icon = item.icon;
          const isDestructive = item.variant === "destructive";
          return (
            <button
              key={item.label}
              type="button"
              data-testid={item.testId}
              onClick={() => {
                setOpen(false);
                item.onClick();
              }}
              className={twMerge(
                "flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3.5 py-2.5 text-[13px] font-semibold transition-colors duration-100",
                isDestructive
                  ? "text-danger-strong hover:bg-danger-wash"
                  : "text-content-primary hover:bg-brand-gray",
              )}
            >
              {Icon && <Icon className="size-3.5" strokeWidth={2} />}
              {item.label}
            </button>
          );
        })}
      </Popover>
    </div>
  );
}
